import { useMutation } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button, TextButton } from '@xing-com/button';
import type { ArticleType } from '@xing-com/crate-articles-article-editor';
import type { Maybe } from '@xing-com/crate-common-graphql-types';
import { useMatchMedia } from '@xing-com/crate-hooks-use-match-media';
import { Dialog } from '@xing-com/dialog';
import { IconEdit, IconTrash } from '@xing-com/icons';
import { mediaConfined } from '@xing-com/layout-tokens';

import { ArticlesDeleteArticleDocument } from '../../graphql/articles-delete-article.gql-types';
import type {
  ArticlesDeleteArticleMutation,
  ArticlesDeleteArticleMutationVariables,
} from '../../graphql/articles-delete-article.gql-types';
import { useUserLoginState } from '../../hooks/user-login-state';
import type { ArticleAuthorType } from '../../types';

import { Styled } from './styles';

type ArticleEditorHeaderProps = {
  show: boolean;
  article: ArticleType;
  author: Maybe<ArticleAuthorType>;
  onError?: () => void;
};

const ArticleEditorHeader: React.FC<ArticleEditorHeaderProps> = ({
  show,
  article,
  author,
  onError,
}) => {
  const intl = useIntl();
  const { isLoggedOut } = useUserLoginState();

  const isTabletOrBigger = useMatchMedia(mediaConfined);

  const [showConfirmationDialog, setShowConfirmationDialog] =
    useState<boolean>(false);

  const [deleteArticle, { loading }] = useMutation<
    ArticlesDeleteArticleMutation,
    ArticlesDeleteArticleMutationVariables
  >(ArticlesDeleteArticleDocument, {
    errorPolicy: 'all',
    variables: {
      globalId: article.globalId,
    },
    onCompleted: (data) => {
      setShowConfirmationDialog(false);

      if (data.articlesDeleteArticle?.__typename === 'ArticlesArticle') {
        if (author?.type === 'insider_page') {
          window.location.assign(`/news/insider-collection/${author.pageName}`);
        } else {
          window.location.assign(author?.pageURL || '/');
        }
      } else {
        onError?.();
      }
    },
    onError: () => onError?.(),
  });

  if (!show || isLoggedOut || !author) {
    return null;
  }

  return (
    <Styled.ArticleEditHeaderContainer data-qa="article-viewer-edit-header">
      <Styled.ArticleEditHeaderMeta size="small">
        <FormattedMessage id="ARTICLES_ARTICLE_VIEWER_VIEW_AS_VISITOR" />
      </Styled.ArticleEditHeaderMeta>

      <Styled.ArticleEditHeaderButtonsContainer>
        <Button
          size="small"
          variant="primary"
          to={`/news/article/${article.slug}/edit`}
          icon={IconEdit}
          aria-label={intl.formatMessage({
            id: 'ARTICLES_ARTICLE_VIEWER_EDIT_BUTTON',
          })}
        >
          {isTabletOrBigger ? (
            <FormattedMessage id="ARTICLES_ARTICLE_VIEWER_EDIT_BUTTON" />
          ) : null}
        </Button>

        <TextButton
          size="small"
          icon={IconTrash}
          onClick={() => setShowConfirmationDialog(true)}
          aria-label={intl.formatMessage({
            id: 'ARTICLES_ARTICLE_VIEWER_DELETE_BUTTON',
          })}
        >
          {isTabletOrBigger ? (
            <FormattedMessage id="ARTICLES_ARTICLE_VIEWER_DELETE_BUTTON" />
          ) : null}
        </TextButton>
      </Styled.ArticleEditHeaderButtonsContainer>

      <Dialog
        show={showConfirmationDialog}
        text={intl.formatMessage({
          id: 'ARTICLES_ARTICLE_VIEWER_DELETE_DIALOG_CONFIRMATION_DESCRIPTION',
        })}
        confirmLabel={intl.formatMessage({
          id: 'ARTICLES_ARTICLE_VIEWER_DELETE_DIALOG_CONFIRM_BUTTON',
        })}
        cancelLabel={intl.formatMessage({
          id: 'ARTICLES_ARTICLE_VIEWER_DELETE_DIALOG_CANCEL_BUTTON',
        })}
        loading={loading}
        onCancel={() => setShowConfirmationDialog(false)}
        onConfirm={() => deleteArticle()}
        isDestructive
        data-qa="article-delete-confirmation-dialog"
      ></Dialog>
    </Styled.ArticleEditHeaderContainer>
  );
};

export default ArticleEditorHeader;
